import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui';
import {
  api, type RadarRow, type RadarSignalsResponse, type RadarActivityRow, type RadarDeveloperContact,
} from '../lib/api';
import DevelopmentLaneRow from '../components/radar/DevelopmentLaneRow';
import DevelopmentLaneHeader from '../components/radar/DevelopmentLaneHeader';
import { BAND_TONE } from '../components/radar/bands';
import {
  DEVELOPMENT_BAND_LABEL, DEVELOPMENT_OUTCOME_LABEL, asSentence, isHandedToResearch, isHiddenBySizeFilter,
  type DevelopmentOutcome,
} from '../components/radar/developmentLane';

/**
 * The Deal Radar worklist.
 *
 * ⭐ HIS CALL 2026-09-20: "choose a slice, then work it top-down." The filters pick the slice and
 *   live in the URL so 2pm resumes where 9am stopped; inside the slice the motion is a queue - the
 *   top row first, a disposition removes it, the next takes its place.
 *
 * ⛔ IT GOES THROUGH `api.*`, NEVER A HAND-ROLLED FETCH. DataHub hand-rolls `fetch` with a raw
 *   `localStorage` token in three places and gets no refresh; copying that is how a screen starts
 *   401ing an hour into a session.
 *
 * ⛔⛔ NO LANDLORD PHONE, EVER, and no `tel:` link. There is no landlord number in this data and the
 *    digest prompt forbids producing one - the screen must not reintroduce what the digest refuses.
 *    And on iOS a third-party app cannot be the default `tel:` handler, so a `tel:` call reaches
 *    the native dialler and is NEVER recorded in GHL. One button, one record.
 */

const LANES: Array<{ key: keyof RadarSignalsResponse & string; lane: string; label: string }> = [
  { key: 'loansPastDue', lane: 'loan_past_due', label: 'Loans past due' },
  { key: 'loansMaturingSoon', lane: 'loan_maturing', label: 'Loans maturing' },
  { key: 'leasesExpiringSoon', lane: 'lease_expiring', label: 'Leases expiring' },
  { key: 'expiredListings', lane: 'expired_listing', label: 'Expired listings' },
  { key: 'freshListings', lane: 'fresh_listing', label: 'Fresh listings' },
  // The CEQA reader's lane (plan Tasks 17-22). Last, so every older lane keeps its place in "All lanes".
  { key: 'newDevelopments', lane: 'new_development', label: 'New development' },
];

const OUTCOMES: Array<{ value: string; label: string }> = [
  { value: 'called', label: 'Called' },
  { value: 'vm_left', label: 'VM left' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'bad_number', label: 'Bad number' },
  { value: 'needs_skiptrace', label: 'Skip-trace' },
];

/**
 * ⛔ THE SAME WORD MEANS A DIFFERENT MEASUREMENT PER LANE - months to expiry on a lease, days to
 *   maturity on a loan, days SINCE expiry on an expired listing. The chip says which.
 */
const BAND_LABEL: Record<string, Record<string, string>> = {
  loan_past_due: { urgent: 'Past due', prime: 'Within 60 days', early: 'Beyond 60 days' },
  loan_maturing: { urgent: 'Past due', prime: 'Within 60 days', early: 'Beyond 60 days' },
  lease_expiring: { urgent: 'Expires 0-6mo', prime: 'Expires 6-12mo', early: 'Expires 12-18mo' },
  expired_listing: { urgent: 'Off market <45d', prime: 'Off market 45-180d', early: 'Off market 180d+' },
  fresh_listing: {},
  // ⭐ D8, his order: approved first - the same words the development row's chip uses, from one map.
  new_development: DEVELOPMENT_BAND_LABEL,
};

const BLOCKER_LABEL: Record<string, string> = {
  no_phone: 'Needs skip-trace - no number',
  no_name: 'Needs a name - number with nobody behind it',
  needs_dnc_check: 'Needs a DNC scrub - 1 credit',
  on_dnc: 'On the Do Not Call register',
};

/** A worked row's outcome in words, whichever lane set it - the undo banner and the activity list read this. */
const outcomeLabel = (outcome: string | undefined): string | undefined =>
  OUTCOMES.find((o) => o.value === outcome)?.label
    ?? (outcome ? DEVELOPMENT_OUTCOME_LABEL[outcome as DevelopmentOutcome] : undefined);

/** "1 smaller project", "3 smaller projects". */
const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** ⛔ Returns null for a missing number. A blank is honest; a zero is a claim nobody made. */
const num = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? Math.round(n).toLocaleString() : null;

const money = (n: number | null | undefined) => {
  const v = num(n);
  return v === null ? null : `$${v}`;
};

const personOn = (r: RadarRow) =>
  r.owner_human_name || r.contact_name || r.owner_name || r.tenant_name || null;

/** The chip that says which side of the deal a named party sits on. */
const PARTY_LABEL: Record<string, string> = {
  tenant: 'Tenant', owner: 'Owner', landlord: 'Landlord',
};

const PARTY_TONE: Record<string, string> = {
  tenant: 'bg-emerald-50 text-emerald-800',
  owner: 'bg-blue-50 text-blue-800',
  landlord: 'bg-amber-50 text-amber-800',
};

/** What this row is about, in the words Robert would use on the phone. */
const signalLine = (r: RadarRow): string => {
  switch (r.signal_type) {
    case 'loan_past_due':
    case 'loan_maturing': {
      const parts = [
        r.maturity_date ? `Loan matures ${r.maturity_date}` : 'Loan',
        money(r.mortgage_amount),
        r.lender_name,
      ].filter(Boolean);
      return parts.join(' · ');
    }
    case 'lease_expiring':
      return [
        r.lease_expiration ? `Lease expires ${r.lease_expiration}` : 'Lease expiring',
        r.months_until != null ? `${r.months_until} months` : null,
        // ⭐⭐ THE FIELD HE OPENED THE LIVE APP AND FOUND MISSING. 568 of 572 rows carry it, the
        //   2,000 SF floor JUDGES on it, and it showed on none of them - so a row could not be
        //   told from a row that should not be in the list at all.
        num(r.sf_occupied) ? `${num(r.sf_occupied)} SF occupied` : null,
        r.tenant_name,
      ].filter(Boolean).join(' · ');
    case 'expired_listing': {
      const dropped =
        r.original_list_price && r.list_price && r.original_list_price > r.list_price
          ? `asked ${money(r.list_price)}, first listed ${money(r.original_list_price)}`
          : `asked ${money(r.list_price) ?? 'unstated'}`;
      // ⚠️ `cumulative_dom` is the live figure; `days_on_market` is frozen at capture time. Prefer
      //   the live one and fall back, rather than showing neither.
      const dom = r.cumulative_dom ?? r.days_on_market;
      return [
        `${r.standard_status} ${r.expiration_date ?? ''}`.trim(),
        dropped,
        num(dom) ? `${num(dom)} days on market` : null,
      ].filter(Boolean).join(' · ');
    }
    default:
      return [money(r.list_price), r.city].filter(Boolean).join(' · ');
  }
};

/**
 * The expired lane's parcel facts, on the row rather than behind a click.
 *
 * ⭐ HIS CALL 2026-09-21: everything on the row, no expander. His own standing rule is **DEFER
 *   PROSE, NEVER A NUMBER** - and every item here is a number or an identifier he would otherwise
 *   have to open the property to find.
 * ⛔⛔ A LITERAL 0 BUILDING SF MEANS NO BUILDING, SO THE LOT IS THE SUBJECT - it does not mean
 *    "too small". 7 rows in his file are a literal zero, and the most expensive listing in it
 *    (Jackson Ave, Murrieta, $14,990,000) is one of them.
 */
const parcelLine = (r: RadarRow): string | null => {
  if (r.signal_type !== 'expired_listing') return null;
  const size = num(r.building_sf)
    ? `${num(r.building_sf)} SF building${num(r.lot_sf) ? ` on ${num(r.lot_sf)} SF lot` : ''}`
    : num(r.lot_sf)
      ? `${num(r.lot_sf)} SF lot`
      : null;
  return [
    size,
    r.county ? `${r.county} County` : null,
    (r.apns ?? []).length ? `APN ${(r.apns ?? []).join(', ')}` : null,
  ].filter(Boolean).join(' · ') || null;
};

export default function DealRadar() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<RadarSignalsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /**
   * ⛔ `half` exists because there are now TWO dispositions per lease row and Undo has to put back
   *   the right one. Without it, undoing a landlord hand-off would un-suppress the TENANT - a
   *   worked row silently returning to the call list for a reason that never happened.
   */
  const [lastMark, setLastMark] = useState<
    { id: string; row: RadarRow; outcome: string; half: 'tenant' | 'landlord'; ghlTagRemovedFailed?: string } | null
  >(null);
  const [workedToday, setWorkedToday] = useState(0);
  /**
   * ⭐ HIS CALL 2026-09-20: he sees dispositions BOTH ways - a block in the 7am digest and this.
   *   Any admin can mark, and every mark names who set it, which is what makes this readable.
   */
  const [tab, setTab] = useState<'worklist' | 'activity'>('worklist');
  /**
   * The inline landlord-contact capture, open on at most one row at a time.
   *
   * ⭐⭐⭐ IT IS WHAT CLOSES THE RESEARCH QUEUE. "Find a leasing contact at Prologis, Inc." was a
   *    job with nowhere to put the answer; this is the answer's home. ⛔ Part 4's AI pre-fills
   *    these same fields and never saves - a person reads the citation and presses save, so every
   *    field stays editable and the save takes what is in the FORM, not what a model said.
   */
  const [captureFor, setCaptureFor] = useState<string | null>(null);
  const [capture, setCapture] = useState({ name: '', title: '', phone: '', email: '', source: '' });
  const [captureError, setCaptureError] = useState<string | null>(null);

  /**
   * What the AI proposed for the row whose form is open, so the citation sits BESIDE the fields a
   * person is about to accept. ⛔ It is never saved by the AI - it fills the form, nothing more.
   */
  const [proposal, setProposal] = useState<
    | { entityId: string; status: string; sourceUrl?: string; ownDomain?: boolean; reason?: string }
    | null
  >(null);

  /**
   * ⛔⛔ AI FIRST, A HUMAN ONLY FOR WHAT IT COULD NOT FIND - his call, 2026-09-21. Three honest
   *    outcomes, and only one of them pre-fills anything:
   *      proposed          - a named person WITH a citation
   *      company_only      - a source but nobody named; the form opens empty for a researcher
   *      nothing / no search - say so plainly rather than open a form implying we found something
   * ⛔ `rejected_no_search` means Claude answered from memory. It is a fabrication however it reads.
   */
  /**
   * A batch research run over the current slice, and the review it produces.
   *
   * ⭐⭐⭐ HIS CALL 2026-09-21: *"batch research, bulk-accept own-domain results only."* 497 leads
   *    reviewed one at a time means nobody reviews them; "accept all" means nobody reads a single
   *    citation. **Own-domain is a checkable FACT about the URL**, which is what makes it a
   *    defensible place to draw that line rather than a confidence number nobody can audit.
   */
  const [batch, setBatch] = useState<
    | {
        running: boolean;
        accepting: boolean;
        accepted: number;
        failed: string[];
        results: Array<{
          entity_id: string;
          status: string;
          proposal: { name: string; title: string | null; phone: string | null; email: string | null; sourceUrl: string; ownDomain: boolean } | null;
          reason?: string;
        }>;
        counts: { proposed: number; ownDomain: number; needsReview: number; companyOnly: number; nothing: number; rejectedNoSearch: number };
      }
    | null
  >(null);

  /** ⛔ The server caps a batch at 25; the screen must not offer a button that would be refused. */
  const BATCH_CAP = 25;

  const researchBatch = async (candidates: RadarRow[]) => {
    const ids = candidates.slice(0, BATCH_CAP).map((r) => r.entity_id);
    setBatch({
      running: true, accepting: false, accepted: 0, failed: [], results: [],
      counts: { proposed: 0, ownDomain: 0, needsReview: 0, companyOnly: 0, nothing: 0, rejectedNoSearch: 0 },
    });
    try {
      const res = await api.researchRadarLandlordBatch(ids);
      setBatch({ running: false, accepting: false, accepted: 0, failed: [], results: res.results, counts: res.counts });
    } catch (e) {
      setBatch(null);
      setError(e instanceof Error ? e.message : 'Could not run that research batch');
    }
  };

  /** Turn one reviewed proposal into a saved contact, through the SAME path a manual save uses. */
  const acceptProposal = async (entityId: string, p: { name: string; title: string | null; phone: string | null; email: string | null; sourceUrl: string }) => {
    await api.saveRadarLandlordContact({
      entity_id: entityId, name: p.name,
      title: p.title ?? undefined, phone: p.phone ?? undefined,
      email: p.email ?? undefined, source_url: p.sourceUrl,
    });
    setData((d) => d && ({
      ...d,
      leasesExpiringSoon: d.leasesExpiringSoon.map((r) =>
        r.entity_id === entityId && r.parties?.landlord
          ? {
              ...r, landlordSuppressed: false, landlordMark: null,
              parties: { ...r.parties, landlord: { ...r.parties.landlord, capturedContact: {
                name: p.name, title: p.title, phone: p.phone, email: p.email,
                sourceUrl: p.sourceUrl, capturedAt: new Date().toISOString(),
              } } },
            }
          : r
      ),
    }));
  };

  /**
   * ⛔⛔ BULK ACCEPT IS OWN-DOMAIN ONLY, and that is enforced HERE as well as described in the
   *    copy. A third-party citation is one somebody has to read, so it never rides along.
   * ⛔ Sequential, and it REPORTS what failed. Each accept writes to the app and to GHL; a silent
   *   partial success would leave nobody knowing which half of the batch landed.
   */
  const acceptOwnDomain = async () => {
    if (!batch) return;
    const own = batch.results.filter((r) => r.status === 'proposed' && r.proposal?.ownDomain);
    setBatch((b) => b && ({ ...b, accepting: true, accepted: 0, failed: [] }));
    let ok = 0;
    const failed: string[] = [];
    for (const r of own) {
      try {
        await acceptProposal(r.entity_id, r.proposal!);
        ok += 1;
      } catch {
        failed.push(r.proposal!.name);
      }
      setBatch((b) => b && ({ ...b, accepted: ok, failed }));
    }
    setBatch((b) => b && ({
      ...b, accepting: false, accepted: ok, failed,
      results: b.results.filter((x) => !(x.status === 'proposed' && x.proposal?.ownDomain && !failed.includes(x.proposal.name))),
    }));
  };

  const researchLandlord = async (row: RadarRow) => {
    setBusy(`research|${row.entity_id}`);
    setCaptureError(null);
    try {
      const { result } = await api.researchRadarLandlord(row.entity_id);
      setProposal({
        entityId: row.entity_id, status: result.status,
        sourceUrl: result.proposal?.sourceUrl, ownDomain: result.proposal?.ownDomain,
        reason: result.reason,
      });
      if (result.status === 'proposed' && result.proposal) {
        setCaptureFor(row.entity_id);
        setCapture({
          name: result.proposal.name,
          title: result.proposal.title ?? '',
          phone: result.proposal.phone ?? '',
          email: result.proposal.email ?? '',
          source: result.proposal.sourceUrl,
        });
      } else if (result.status === 'company_only') {
        // A source but no person: open the form so a researcher finishes the job from there.
        setCaptureFor(row.entity_id);
        setCapture({ name: '', title: '', phone: '', email: '', source: result.proposal?.sourceUrl ?? '' });
      } else {
        setCaptureFor(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not research that landlord');
    } finally {
      setBusy(null);
    }
  };

  const openCapture = (entityId: string) => {
    setCaptureFor(entityId);
    setCapture({ name: '', title: '', phone: '', email: '', source: '' });
    setCaptureError(null);
    setProposal(null);
  };

  const saveCapture = async (row: RadarRow) => {
    /**
     * ⛔ A NAME IS THE MINIMUM, and the API refuses without one too. A bare number with nobody
     *   behind it is not a contact - it is the `no_name` blocker this board already names, and
     *   saving it would make a row look callable while nobody knows who answers.
     */
    if (!capture.name.trim()) {
      setCaptureError('A name is required - a number with nobody behind it is not a contact');
      return;
    }
    setBusy(`capture|${row.entity_id}`);
    try {
      const res = await api.saveRadarLandlordContact({
        entity_id: row.entity_id,
        name: capture.name.trim(),
        title: capture.title.trim() || undefined,
        phone: capture.phone.trim() || undefined,
        email: capture.email.trim() || undefined,
        source_url: capture.source.trim() || undefined,
      });
      setCaptureFor(null);
      setData((d) => d && ({
        ...d,
        leasesExpiringSoon: d.leasesExpiringSoon.map((r) =>
          r.entity_id === row.entity_id && r.parties?.landlord
            ? {
                ...r,
                // The lead is answered, so it is no longer out for research.
                landlordSuppressed: false, landlordMark: null,
                parties: { ...r.parties, landlord: { ...r.parties.landlord, capturedContact: {
                  name: res.contact.name, title: res.contact.title, phone: res.contact.phone,
                  email: res.contact.email, sourceUrl: res.contact.sourceUrl, capturedAt: new Date().toISOString(),
                } } },
              }
            : r
        ),
      }));
      // ⛔ A GHL failure does not lose the contact - it is already saved in the app. Say so.
      if (res.ghlError) setError(`Saved here, but GHL refused it (${res.ghlError})`);
    } catch (e) {
      setCaptureError(e instanceof Error ? e.message : 'Could not save that contact');
    } finally {
      setBusy(null);
    }
  };
  const [activity, setActivity] = useState<RadarActivityRow[] | null>(null);

  // ⭐ The slice lives in the URL, so a reload - or 2pm after a 9am start - resumes it.
  const lane = params.get('lane') ?? '';
  const band = params.get('band') ?? '';
  const city = params.get('city') ?? '';
  const readiness = params.get('readiness') ?? '';
  /**
   * ⭐ D4 and plan decision 12: the development lane's two hidden sets are shown by a press, and the press lives
   *   in the URL like every other part of the slice, so a reload keeps it.
   */
  const showSmall = params.get('small') === 'shown';
  const showHanded = params.get('handed') === 'shown';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getRadarSignals({ lane, band, city, readiness }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the radar');
    } finally {
      setLoading(false);
    }
  }, [lane, band, city, readiness]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (tab !== 'activity' || activity) return;
    void api.getRadarActivity()
      .then((r) => setActivity(r.activity))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load activity'));
  }, [tab, activity]);

  /**
   * The slice BEFORE worked rows are taken out, so each hidden set can be COUNTED by the control that hides it
   * (spec 9.4, D4 - "a control must NAME its contents").
   */
  const sliced = useMemo(() => {
    if (!data) return [];
    const wanted = lane ? LANES.filter((l) => l.lane === lane) : LANES;
    return wanted
      .flatMap((l) => (data[l.key] as RadarRow[] | undefined) ?? [])
      .filter((r) => !band || r.band === band)
      .filter((r) => !city || (r.city ?? '').toLowerCase().includes(city.toLowerCase()))
      .filter((r) => !readiness || r.readiness === readiness);
  }, [data, lane, band, city, readiness]);

  /**
   * Open development projects STATED and under both size floors (D4, the size floor ruling) - hidden by default in
   * every view, counted. ⛔ A project with a size unstated is never in here: nothing proves it small.
   */
  const smallRows = useMemo(
    () => sliced.filter((r) => r.signal_type === 'new_development' && !r.suppressed && isHiddenBySizeFilter(r)),
    [sliced]
  );

  /** Development projects handed to research (plan decision 12) - gone like any worked row, counted. */
  const handedRows = useMemo(() => sliced.filter(isHandedToResearch), [sliced]);

  /** Every row across the chosen lanes, suppressed ones removed, ranked most urgent first. */
  const rows = useMemo(() => {
    const order: Record<string, number> = { urgent: 0, prime: 1, early: 2 };
    const small = new Set(smallRows);
    return sliced
      /**
       * ⭐⭐⭐ HIS CALL 2026-09-21: **the row stays until BOTH halves are worked.** *"we want both
       *    at end of day."* A lease naming a landlord carries two independent leads; neither may
       *    vanish because the other was done. A lease with no landlord behaves exactly as before -
       *    worked means gone.
       */
      .filter((r) => !r.suppressed
        || (!!r.parties?.landlord && !r.landlordSuppressed)
        || (showHanded && isHandedToResearch(r)))
      .filter((r) => showSmall || !small.has(r))
      /**
       * ⭐ C7: a STABLE sort by band keeps the server's order inside a band - on the development lane that is
       *   newest filing first (`ceqa_lane_rows`), so approved projects read newest first, then in review.
       */
      .sort((a, b) => (order[a.band ?? 'early'] ?? 3) - (order[b.band ?? 'early'] ?? 3));
  }, [sliced, smallRows, showSmall, showHanded]);

  /**
   * The landlord leads in this slice that nobody has researched yet.
   * ⛔ A row whose contact is already captured is NOT a candidate - re-researching it would spend
   *   a metered search to re-derive a fact a human already verified.
   */
  const researchCandidates = useMemo(
    () => rows.filter((r) => r.parties?.landlord && !r.parties.landlord.capturedContact),
    [rows]
  );

  const cities = useMemo(
    () => [...new Set(rows.map((r) => r.city).filter(Boolean) as string[])].sort(),
    [rows]
  );

  const mark = async (row: RadarRow, outcome: string) => {
    const key = `${row.signal_type}|${row.entity_id}`;
    setBusy(key);
    try {
      const res = await api.markRadarSignal({
        signal_type: row.signal_type, entity_id: row.entity_id, outcome,
      });
      setLastMark({ id: res.mark.id, row, outcome, half: 'tenant' });
      setWorkedToday((n) => n + 1);
      // Remove it locally so the queue advances without a full refetch.
      setData((d) => {
        if (!d) return d;
        const laneKey = LANES.find((l) => l.lane === row.signal_type)?.key;
        if (!laneKey) return d;
        return {
          ...d,
          [laneKey]: (d[laneKey] as RadarRow[]).map((r) =>
            r.entity_id === row.entity_id ? { ...r, suppressed: true, mark: { id: res.mark.id, outcome } } : r
          ),
        };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record that');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Hand the landlord to research.
   *
   * ⛔⛔⛔ IT WRITES `lease_landlord` / `landlord_research` AND NOTHING ELSE. The API refuses any
   *    other pairing (`outcomesFor` in radarController), because there is no landlord phone in
   *    this data and "somebody called the landlord" must not be a recordable fact.
   * ⛔ It touches `landlordSuppressed`, never `suppressed` - the tenant half is a different lead.
   */
  const markLandlord = async (row: RadarRow) => {
    const key = `landlord|${row.entity_id}`;
    setBusy(key);
    try {
      const res = await api.markRadarSignal({
        signal_type: 'lease_landlord', entity_id: row.entity_id, outcome: 'landlord_research',
      });
      setLastMark({ id: res.mark.id, row, outcome: 'landlord_research', half: 'landlord' });
      setWorkedToday((n) => n + 1);
      setData((d) => d && ({
        ...d,
        leasesExpiringSoon: d.leasesExpiringSoon.map((r) =>
          r.entity_id === row.entity_id
            ? { ...r, landlordSuppressed: true, landlordMark: { id: res.mark.id, outcome: 'landlord_research' } }
            : r
        ),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record that');
    } finally {
      setBusy(null);
    }
  };

  /**
   * A developer contact a person saved (plan Task 18, decision 9). The server cleared a research hand-off on the
   * project - only that, never "not a fit" - so the row is back on the list, showing who to reach.
   */
  const developerCaptured = (entityId: string, contact: RadarDeveloperContact) => {
    setData((d) => d && ({
      ...d,
      newDevelopments: (d.newDevelopments ?? []).map((r) => {
        if (r.entity_id !== entityId) return r;
        const handedOff = r.mark?.outcome === 'developer_research';
        return { ...r, capturedContact: contact, ...(handedOff ? { suppressed: false, mark: null } : {}) };
      }),
    }));
    // ⛔ The hand-off the banner offers to undo no longer exists on the server, so Undo must not be offered.
    setLastMark((m) => (m && m.row.entity_id === entityId && m.outcome === 'developer_research' ? null : m));
  };

  const undo = async () => {
    if (!lastMark) return;
    setBusy('undo');
    try {
      const res = await api.undoRadarMark(lastMark.id);
      const row = lastMark.row;
      setData((d) => {
        if (!d) return d;
        const laneKey = LANES.find((l) => l.lane === row.signal_type)?.key;
        if (!laneKey) return d;
        // ⛔ Put back the half that was marked, never the other one.
        const restore = (r: RadarRow) =>
          lastMark.half === 'landlord'
            ? { ...r, landlordSuppressed: false, landlordMark: null }
            : { ...r, suppressed: false, mark: null };
        return {
          ...d,
          [laneKey]: (d[laneKey] as RadarRow[]).map((r) =>
            r.entity_id === row.entity_id ? restore(r) : r
          ),
        };
      });
      setWorkedToday((n) => Math.max(0, n - 1));
      // ⛔ A SILENT HALF-UNDO IS WORSE THAN A LOUD ONE. If GHL refused, the row is back here while
      //   the tag is still over there, and the only honest thing to do is say so.
      setLastMark(res.ghlTagRemoved ? null : { ...lastMark, ghlTagRemovedFailed: res.ghlError ?? 'unknown' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not undo that');
    } finally {
      setBusy(null);
    }
  };

  const call = async (row: RadarRow) => {
    setBusy(`call|${row.entity_id}`);
    try {
      const res = await api.callRadarSignal(row.signal_type, row.entity_id);
      if (res.ghlUrl) window.open(res.ghlUrl, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the contact in GHL');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-6">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Deal radar</h1>
            <p className="mt-1 text-[13px] text-gray-500">
              A worked row is gone until its signal changes. When it comes back, it says why.
            </p>
          </div>
          <div className="text-[13px] text-gray-600">
            <span data-testid="worked-today" className="font-medium text-gray-900">{workedToday}</span> worked today
          </div>
        </div>

        <div className="mt-4 flex gap-1 border-b border-gray-200">
          {(['worklist', 'activity'] as const).map((t) => (
            <button key={t} type="button" onClick={() => setTab(t)}
              className={`px-3 py-2 text-[13px] ${
                tab === t
                  ? 'border-b-2 border-blue-600 font-medium text-blue-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}>
              {t === 'worklist' ? 'Worklist' : 'Activity'}
            </button>
          ))}
        </div>

        {tab === 'activity' && (
          <div className="mt-4">
            {!activity && <p className="text-[13px] text-gray-500">Loading activity...</p>}
            {activity && activity.length === 0 && (
              <p className="text-[13px] text-gray-500">Nothing has been worked yet.</p>
            )}
            {activity && activity.length > 0 && (
              <Card>
                <CardContent className="py-2">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left text-[12px] uppercase tracking-wide text-gray-400">
                        <th className="py-2 pr-3">When</th>
                        <th className="py-2 pr-3">Who</th>
                        <th className="py-2 pr-3">Outcome</th>
                        <th className="py-2 pr-3">Lane</th>
                        <th className="py-2">What it was about at the time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((a) => (
                        <tr key={a.id} data-testid="activity-row" className="border-t border-gray-100">
                          <td className="py-2 pr-3 text-gray-600">
                            {new Date(a.marked_at).toLocaleString()}
                          </td>
                          <td className="py-2 pr-3 text-gray-900">{a.marked_by_name ?? 'Unknown'}</td>
                          <td className="py-2 pr-3">
                            {outcomeLabel(a.outcome) ?? a.outcome}
                          </td>
                          <td className="py-2 pr-3 text-gray-600">
                            {LANES.find((l) => l.lane === a.signal_type)?.label ?? a.signal_type}
                          </td>
                          {/* ⭐ The SNAPSHOT, which is the point: a mark is a record of what you
                              called ABOUT, not a flag - and it is what "has it changed?" compares. */}
                          <td className="py-2 text-gray-600">
                            {[a.band_at_mark, a.value_at_mark].filter(Boolean).join(' · ') || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {tab === 'worklist' && (<>
        <Card className="mt-4">
          <CardContent className="py-3">
            <div className="text-[11.5px] uppercase tracking-wide text-gray-400">
              Choose a slice - it stays in the address bar, so a reload resumes it
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select aria-label="Lane" className="rounded border border-gray-300 px-2 py-1 text-[13px]"
                value={lane} onChange={(e) => setParam('lane', e.target.value)}>
                <option value="">All lanes</option>
                {LANES.map((l) => <option key={l.lane} value={l.lane}>{l.label}</option>)}
              </select>
              <select aria-label="Band" className="rounded border border-gray-300 px-2 py-1 text-[13px]"
                value={band} onChange={(e) => setParam('band', e.target.value)}>
                <option value="">Any urgency</option>
                <option value="urgent">{lane === 'new_development' ? DEVELOPMENT_BAND_LABEL.urgent : 'Urgent'}</option>
                <option value="prime">{lane === 'new_development' ? DEVELOPMENT_BAND_LABEL.prime : 'Prime'}</option>
                <option value="early">{lane === 'new_development' ? DEVELOPMENT_BAND_LABEL.early : 'Early'}</option>
              </select>
              <select aria-label="City" className="rounded border border-gray-300 px-2 py-1 text-[13px]"
                value={city} onChange={(e) => setParam('city', e.target.value)}>
                <option value="">Any city</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select aria-label="Readiness" className="rounded border border-gray-300 px-2 py-1 text-[13px]"
                value={readiness} onChange={(e) => setParam('readiness', e.target.value)}>
                <option value="">Ready and research</option>
                <option value="ready_to_call">Ready to call</option>
                <option value="research_needed">Research</option>
              </select>
              <button type="button" onClick={() => void load()}
                className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-700 hover:bg-gray-50">
                Refresh
              </button>
              {/*
                ⭐ Names exactly how many it will do, because the server caps a batch at 25 and a
                  button that promises "all 155" and silently does 25 is how people stop trusting
                  a screen. ⛔ Metered - nothing is spent until this is pressed.
              */}
              {researchCandidates.length > 0 && (
                <button type="button" data-testid="research-batch"
                  onClick={() => void researchBatch(researchCandidates)} disabled={!!batch?.running}
                  className="rounded border border-violet-300 bg-violet-50 px-2 py-1 text-[13px] text-violet-900 hover:bg-violet-100 disabled:opacity-50">
                  {batch?.running
                    ? 'Searching...'
                    : `Find contacts with AI (${Math.min(researchCandidates.length, BATCH_CAP)} of ${researchCandidates.length})`}
                </button>
              )}
            </div>
            {data && (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-gray-600">
                <span><span className="font-medium text-gray-900">{rows.length}</span> in this slice</span>
                <span className="text-gray-300">·</span>
                <span><span className="font-medium text-gray-900">
                  {rows.filter((r) => r.readiness === 'ready_to_call').length}
                </span> ready to call</span>
                <span className="text-gray-300">·</span>
                <span>{rows.filter((r) => r.readiness !== 'ready_to_call').length} research</span>
              </div>
            )}
            {/*
              ⭐ D4 and plan decision 12: two sets the development lane hides by default, each COUNTED by the
                control that hides it. A hidden row nobody can count reads as a row that is gone.
            */}
            {(smallRows.length > 0 || handedRows.length > 0) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
                {smallRows.length > 0 && (
                  <button type="button" data-testid="toggle-small"
                    onClick={() => setParam('small', showSmall ? '' : 'shown')}
                    className="text-blue-700 hover:underline">
                    {showSmall
                      ? `Showing ${plural(smallRows.length, 'smaller project')} - hide`
                      : `${plural(smallRows.length, 'smaller project')} hidden - show`}
                  </button>
                )}
                {handedRows.length > 0 && (
                  <button type="button" data-testid="toggle-handed"
                    onClick={() => setParam('handed', showHanded ? '' : 'shown')}
                    className="text-blue-700 hover:underline">
                    {showHanded
                      ? `Showing ${handedRows.length} handed to research - hide`
                      : `${handedRows.length} handed to research - show`}
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/*
          ⛔ Plan decision 11: a lane that FAILED is named - never shown as an empty lane, which reads as "nothing
            to do". The other lanes still loaded. The server's sentence is ended once, never "..".
        */}
        {data?.laneErrors && Object.keys(data.laneErrors).length > 0 && (
          <div data-testid="lane-errors"
            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-900">
            {Object.entries(data.laneErrors).map(([laneName, message]) => (
              <div key={laneName}>{asSentence(message)} The other lanes are current.</div>
            ))}
          </div>
        )}

        {lane === 'new_development' && <DevelopmentLaneHeader />}

        {error && (
          <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-800">
            {error}
          </div>
        )}

        {/*
          ⭐⭐⭐ THE REVIEW LIST. His call: bulk-accept ONLY what was cited from the landlord's own
             site; everything else gets an individual look. 497 leads reviewed one at a time means
             nobody reviews them, and "accept all" means nobody reads a citation - own-domain is
             the one split that is a checkable FACT rather than a number nobody can audit.
        */}
        {batch && !batch.running && (
          <Card className="mt-3" data-testid="batch-review">
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[13px] text-gray-700">
                  Researched <span className="font-medium text-gray-900">{batch.results.length}</span> landlord leads -{' '}
                  <span className="font-medium text-emerald-800">{batch.counts.ownDomain}</span> from the landlord's own site,{' '}
                  <span className="font-medium text-amber-800">{batch.counts.needsReview}</span> needing a look,{' '}
                  {batch.counts.companyOnly + batch.counts.nothing} with nothing to save
                  {batch.counts.rejectedNoSearch > 0 && (
                    <span className="text-red-700">, {batch.counts.rejectedNoSearch} discarded as answered from memory</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {batch.counts.ownDomain > 0 && (
                    <button type="button" data-testid="accept-own-domain"
                      onClick={() => void acceptOwnDomain()} disabled={batch.accepting}
                      className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[13px] font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">
                      {batch.accepting
                        ? `Saving ${batch.accepted} of ${batch.counts.ownDomain}...`
                        : `Accept ${batch.counts.ownDomain} from their own sites`}
                    </button>
                  )}
                  <button type="button" onClick={() => setBatch(null)}
                    className="rounded px-2 py-1 text-[13px] text-gray-500 hover:text-gray-700">Dismiss</button>
                </div>
              </div>

              {/* ⛔ A silent partial success would leave nobody knowing which half landed. */}
              {batch.failed.length > 0 && (
                <div data-testid="batch-failures" className="mt-2 text-[12.5px] text-red-700">
                  Could not save {batch.failed.length}: {batch.failed.join(', ')}. They are still listed below.
                </div>
              )}

              {/*
                ⛔⛔ THE THIRD-PARTY ONES, EACH WITH ITS CITATION AND ITS OWN BUTTON. These are the
                   results nobody may wave through - the whole reason the own-domain split exists.
              */}
              {batch.results.filter((r) => r.status === 'proposed' && !r.proposal?.ownDomain).length > 0 && (
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <div className="text-[11.5px] uppercase tracking-wide text-gray-400">
                    Found on third-party pages - read the source before saving
                  </div>
                  {batch.results.filter((r) => r.status === 'proposed' && !r.proposal?.ownDomain).map((r) => (
                    <div key={r.entity_id} data-testid="batch-needs-review"
                      className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                      <span className="text-gray-900">{r.proposal!.name}</span>
                      {r.proposal!.title && <span className="text-gray-600">{r.proposal!.title}</span>}
                      {r.proposal!.phone && <span className="font-mono">{r.proposal!.phone}</span>}
                      <a href={r.proposal!.sourceUrl} target="_blank" rel="noopener noreferrer"
                        className="text-blue-700 hover:underline">source</a>
                      <button type="button" data-testid="accept-one"
                        onClick={() => void acceptProposal(r.entity_id, r.proposal!).then(
                          () => setBatch((b) => b && ({ ...b, results: b.results.filter((x) => x.entity_id !== r.entity_id) })),
                          () => setError('Could not save that contact')
                        )}
                        className="rounded border border-gray-300 px-2 py-0.5 text-[12.5px] text-gray-700 hover:bg-gray-50">
                        Save this one
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {lastMark && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-[13px]">
            <span className="text-gray-700">
              {lastMark.half === 'landlord' ? (<>
                Handed <span className="font-medium text-gray-900">
                  {lastMark.row.parties?.landlord?.name ?? 'the landlord'}
                </span>{' '}to research on {lastMark.row.address ?? 'that row'}
              </>) : lastMark.row.signal_type === 'new_development' ? (<>
                {/* A project has a name, not an address - say which one was worked, in words. */}
                {lastMark.outcome === 'developer_research' ? 'Handed ' : 'Marked '}
                <span className="font-medium text-gray-900">{lastMark.row.name ?? 'that project'}</span>
                {lastMark.outcome === 'developer_research' ? ' to research' : ' not a fit'}
              </>) : (<>
                Marked <span className="font-medium text-gray-900">
                  {outcomeLabel(lastMark.outcome)}
                </span>{' '}on {lastMark.row.address ?? 'that row'}
              </>)}
            </span>
            {lastMark.ghlTagRemovedFailed && (
              <span className="text-red-700">
                Could not remove the tag in GHL ({lastMark.ghlTagRemovedFailed}) - it is still on the contact.
              </span>
            )}
            <span className="flex-1" />
            {!lastMark.ghlTagRemovedFailed && (
              <button type="button" onClick={() => void undo()} disabled={busy === 'undo'}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Undo
              </button>
            )}
            <button type="button" onClick={() => setLastMark(null)}
              className="rounded px-2 py-1 text-gray-500 hover:text-gray-700">Dismiss</button>
          </div>
        )}

        {loading && <p className="mt-6 text-[13px] text-gray-500">Loading the radar...</p>}

        {!loading && rows.length === 0 && data && (
          <p className="mt-6 text-[13px] text-gray-500">
            Nothing in this slice. Widen a filter, or everything here has been worked.
          </p>
        )}

        <div className="mt-3 space-y-2.5">
          {rows.map((r) => {
            const key = `${r.signal_type}|${r.entity_id}`;
            /**
             * ⭐ The development lane renders its own row (spec 9.2) - but through the page's `mark`, so Undo
             *   and the queue's local removal work exactly as on every other lane.
             */
            if (r.signal_type === 'new_development') {
              return (
                <DevelopmentLaneRow key={key} row={r} busy={busy === key}
                  onMark={(row, outcome) => void mark(row, outcome)} onCaptured={developerCaptured} />
              );
            }
            const person = personOn(r);
            const callable = r.readiness === 'ready_to_call' && !!r.phone;
            /**
             * ⭐ Composed on the SERVER (`radarParties`), never re-derived here. Two functions
             *   answering "who is this row about" differently is the 94th's P0 and the 95th's.
             * ⛔ The fallback keeps an older payload rendering rather than blanking the row.
             */
            const contact = r.parties?.contact
              ?? (person || r.phone ? { kind: 'owner' as const, name: person, phone: r.phone } : null);
            const landlord = r.parties?.landlord
              ?? (r.landlord
                ? { name: r.landlord, rep: r.landlord_representative ?? null, capturedContact: null }
                : null);
            /**
             * ⭐ The landlord LEAD only exists where the server composed one. `landlord` above may
             *   fall back for display on an older payload; a lead you can DISPOSITION must come
             *   from the composer, so a mark is never offered against a signal the server cannot
             *   resolve - the 95th's P0, where 337 rows had working buttons that all 404'd.
             */
            const landlordLead = r.parties?.landlord ?? null;
            const tenantOpen = !r.suppressed;
            const landlordOpen = !!landlordLead && !r.landlordSuppressed;
            return (
              <Card key={key} data-testid="radar-row">
                <CardContent className="py-3.5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[15px] font-medium text-gray-900">
                        {r.address ?? 'Address unknown'}{r.city ? `, ${r.city}` : ''}
                      </div>
                      <div className="mt-0.5 text-[13px] text-gray-600">{signalLine(r)}</div>
                      {parcelLine(r) && (
                        <div className="mt-0.5 text-[13px] text-gray-600">{parcelLine(r)}</div>
                      )}
                      {(r.timesFailed ?? 0) > 1 && (
                        <div className="mt-0.5 text-[13px] font-medium text-orange-700">
                          This parcel has failed {r.timesFailed} times
                        </div>
                      )}
                    </div>
                    {r.band && (
                      <span className={`whitespace-nowrap rounded border px-2 py-0.5 text-[12px] ${BAND_TONE[r.band] ?? ''}`}>
                        {BAND_LABEL[r.signal_type]?.[r.band] ?? r.band}
                      </span>
                    )}
                  </div>

                  {r.returnReason && (
                    <div className="mt-2 inline-block rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[12.5px] text-amber-800">
                      Back on the list - {r.returnReason}
                    </div>
                  )}

                  {/*
                    ⛔⛔ HIS QUESTION, 2026-09-21: *"how can i tell if its a tenant or landlord since
                       we want both at end of day right?"* He could not. The row showed a person and
                       a number, then separately a landlord name, and never said which party the
                       number belonged to. ⭐ On a lease row the number is ALWAYS the tenant's,
                       because no landlord phone exists anywhere in this data.
                    ⭐ The party comes from the SERVER's one composer, so this chip, the GHL tag and
                       the note on the contact cannot disagree.
                  */}
                  <div className="mt-2 border-t border-gray-100 pt-2 text-[13px] text-gray-600">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {contact ? (
                        <>
                          <span data-testid={`party-${contact.kind}`}
                            className={`rounded px-1.5 py-0.5 text-[11.5px] uppercase tracking-wide ${PARTY_TONE[contact.kind]}`}>
                            {PARTY_LABEL[contact.kind]}
                          </span>
                          <span className="text-gray-900">{contact.name ?? 'Name not on file'}</span>
                          {r.owner_title && <span className="text-gray-500">{r.owner_title}</span>}
                          {/* The entity behind the human, when they are different things. */}
                          {contact.kind === 'owner' && r.owner_name && r.owner_name !== contact.name && (
                            <span className="text-gray-500">{r.owner_name}</span>
                          )}
                          {/* ⛔ The number is selectable TEXT, never a tel: link - on iOS a tel:
                              call reaches the native dialler and is NEVER recorded in GHL. */}
                          {contact.phone && <span className="font-mono">{contact.phone}</span>}
                          {r.phone_kind && (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[12px] text-gray-600">
                              {r.phone_kind === 'direct' ? 'direct line' : 'switchboard'}
                            </span>
                          )}
                          {r.owner_email && <span className="text-gray-500">{r.owner_email}</span>}
                        </>
                      ) : (
                        !person && <span className="text-gray-500">No contact on file</span>
                      )}
                      {r.unsized && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[12px] text-gray-500">size unknown</span>
                      )}
                    </div>

                    {/*
                      ⛔⛔⛔ A SECOND LEAD, NEVER A SECOND CALL. `landlord` carries no phone FIELD at
                         all - the server type has nowhere to put one - so this block cannot render
                         a number however it is edited later.
                    */}
                    {landlord && (
                      <div data-testid="party-landlord" className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className={`rounded px-1.5 py-0.5 text-[11.5px] uppercase tracking-wide ${PARTY_TONE.landlord}`}>
                          {PARTY_LABEL.landlord}
                        </span>
                        <span className="text-gray-900">{landlord.name}</span>
                        {landlord.rep && <span className="text-gray-500">rep {landlord.rep}</span>}
                        {!landlord.capturedContact && <span className="text-gray-400">no number on file</span>}
                      </div>
                    )}

                    {/*
                      ⭐⭐⭐ THE ONLY PLACE A LANDLORD NUMBER APPEARS, and it is here because a HUMAN
                         researched and saved it. The entity above still has nowhere to put one.
                      ⭐⭐ THE SOURCE STAYS ON THE ROW. It is what separates a contact somebody
                         found from one a model invented, and it is the thing to click when the
                         number turns out to be wrong.
                    */}
                    {landlord?.capturedContact && (
                      <div data-testid="landlord-captured"
                        className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-1">
                        <span className="text-[11.5px] uppercase tracking-wide text-emerald-800">Leasing contact</span>
                        <span className="text-gray-900">{landlord.capturedContact.name}</span>
                        {landlord.capturedContact.title && (
                          <span className="text-gray-600">{landlord.capturedContact.title}</span>
                        )}
                        {landlord.capturedContact.phone && (
                          <span className="font-mono">{landlord.capturedContact.phone}</span>
                        )}
                        {landlord.capturedContact.email && (
                          <span className="text-gray-600">{landlord.capturedContact.email}</span>
                        )}
                        {landlord.capturedContact.sourceUrl && (
                          <a data-testid="landlord-contact-source-link" href={landlord.capturedContact.sourceUrl}
                            target="_blank" rel="noopener noreferrer"
                            className="text-blue-700 hover:underline">source</a>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ⛔ null = not in GHL at all. [] = in GHL with no campaign tags. Different answers. */}
                  {r.activeCampaigns === null ? (
                    <div className="mt-1 text-[12.5px] text-gray-500">Not in GHL yet</div>
                  ) : r.activeCampaigns.length > 0 ? (
                    <div className="mt-1 text-[12.5px] text-amber-800">
                      Already tagged for {r.activeCampaigns.join(', ')}
                    </div>
                  ) : null}

                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2.5">
                    {tenantOpen ? (<>
                      {callable ? (
                        <button type="button" data-testid="call-tenant" onClick={() => void call(r)}
                          disabled={busy === `call|${r.entity_id}`}
                          className="rounded border border-blue-300 bg-blue-50 px-3 py-1.5 text-[13px] font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50">
                          Call the {contact?.kind === 'owner' ? 'owner' : 'tenant'} in GHL
                        </button>
                      ) : (
                        <span className="py-1.5 pr-1 text-[13px] text-gray-500">
                          {BLOCKER_LABEL[r.blocker ?? ''] ?? 'Research - not callable yet'}
                        </span>
                      )}
                      <span className="w-2" />
                      {OUTCOMES.filter((o) => callable || o.value === 'needs_skiptrace' || o.value === 'bad_number')
                        .map((o) => (
                          <button key={o.value} type="button" onClick={() => void mark(r, o.value)} disabled={busy === key}
                            className="rounded border border-gray-300 px-2.5 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                            {o.label}
                          </button>
                        ))}
                    </>) : (
                      /* ⛔ SAYS SO, so nobody rings a tenant who was called this morning. */
                      <span data-testid="tenant-half-done" className="py-1.5 text-[13px] text-gray-500">
                        {PARTY_LABEL[contact?.kind ?? 'tenant']}{' '}
                        {OUTCOMES.find((o) => o.value === r.mark?.outcome)?.label?.toLowerCase() ?? 'worked'}
                        {' '}already
                      </span>
                    )}

                    {/*
                      ⭐⭐⭐ THE SECOND LEAD. *"find a leasing contact at Prologis, Inc."* is a job a
                         researcher can actually finish; "research" on its own is not. His 293
                         landlord-named rows with no callable tenant read as dead ends until this
                         button existed.
                      ⛔⛔ THE VERB IS DELIBERATELY NOT "CALL". There is no landlord number in this
                         data, and two actions side by side is exactly where that rule breaks by
                         accident.
                    */}
                    {/* ⛔ Once the contact is known there is nothing left to research or add. */}
                    {landlordLead && !landlordLead.capturedContact && (<>
                      {landlordOpen ? (
                        <button type="button" data-testid="research-landlord"
                          onClick={() => void markLandlord(r)} disabled={busy === `landlord|${r.entity_id}`}
                          className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-[13px] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                          Find a leasing contact at {landlordLead.name}
                        </button>
                      ) : (
                        <span data-testid="landlord-half-done" className="py-1.5 text-[13px] text-gray-500">
                          Landlord handed to research
                        </span>
                      )}
                      {/*
                        ⭐ Offered whether or not the lead was handed off, because the ANSWER can
                          arrive either way - the researcher who was given the job, or whoever
                          already knew the person. A queue you cannot close is not a queue.
                      */}
                      {/*
                        ⭐⭐ HIS CALL: AI researches FIRST, a human only picks up what it could not
                           find. It PROPOSES into the form below; it never saves.
                        ⛔ Metered, so it runs per click. Nothing costs anything until pressed.
                      */}
                      <button type="button" data-testid="research-landlord-ai"
                        onClick={() => void researchLandlord(r)} disabled={busy === `research|${r.entity_id}`}
                        className="rounded border border-violet-300 bg-violet-50 px-2.5 py-1.5 text-[13px] text-violet-900 hover:bg-violet-100 disabled:opacity-50">
                        {busy === `research|${r.entity_id}` ? 'Searching...' : 'Find them with AI'}
                      </button>
                      <button type="button" data-testid="add-landlord-contact"
                        onClick={() => openCapture(r.entity_id)}
                        className="rounded border border-gray-300 px-2.5 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50">
                        Add the contact
                      </button>
                    </>)}
                    {/*
                      ⛔ `/data-hub/:id`, NOT `/properties/:id`. This is a `master_properties` id;
                         the analyzer route takes a DIFFERENT table's id and PropertyDetail states
                         outright that the two have no key between them, so the obvious-looking
                         route opens the wrong record or nothing at all.
                      ⭐ His ask: *"master_property_id is carried on EVERY lane and used NOWHERE.
                         There is no way to open the property from a radar row."*
                    */}
                    {r.master_property_id && (
                      <>
                        <span className="flex-1" />
                        <a data-testid="open-property" href={`/data-hub/${r.master_property_id}`}
                          className="rounded px-2 py-1.5 text-[13px] text-blue-700 hover:underline">
                          Open the property
                        </a>
                      </>
                    )}
                  </div>

                  {/*
                    ⭐⭐ EVERY FIELD IS EDITABLE, WHICH IS THE POINT. Part 4's AI pre-fills exactly
                       these inputs with a citation beside them; a person reads it and presses
                       save. The save takes what is in the FORM, never what the model said - so a
                       wrong title or a stale number is corrected before it reaches the CRM.
                  */}
                  {/*
                    ⛔⛔ AN HONEST NOTHING IS A RESULT. Opening an empty form after a failed search
                       would imply we found something; saying "no search ran, so that answer came
                       from memory" is the whole reason the guard exists.
                  */}
                  {proposal?.entityId === r.entity_id && proposal.status !== 'proposed' && (
                    <div data-testid="research-outcome"
                      className="mt-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-[12.5px] text-violet-900">
                      {proposal.status === 'company_only' && 'Found the company but nobody named. Over to a researcher - the source is filled in below.'}
                      {proposal.status === 'nothing' && 'No leasing contact found with a source. Over to a researcher.'}
                      {proposal.status === 'rejected_no_search' && 'Discarded: no web search ran, so that answer came from memory rather than a source.'}
                      {proposal.reason ? ` (${proposal.reason})` : ''}
                    </div>
                  )}

                  {captureFor === r.entity_id && landlordLead && (
                    <div data-testid="landlord-contact-form"
                      className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2.5">
                      <div className="text-[12.5px] text-gray-600">
                        Leasing contact at <span className="font-medium text-gray-900">{landlordLead.name}</span>
                        {landlordLead.rep ? ` (rep ${landlordLead.rep})` : ''}
                      </div>
                      {/*
                        ⭐⭐⭐ THE CITATION SITS BESIDE THE FIELDS SOMEBODY IS ABOUT TO ACCEPT. That
                           is the entire safety argument for letting AI fill this in: a person can
                           click the source before pressing save.
                        ⛔ `ownDomain` is a FACT about the URL - it came from the landlord's own
                           site or it did not. It is not a score and must never be shown as one.
                      */}
                      {proposal?.entityId === r.entity_id && proposal.sourceUrl && (
                        <div data-testid="proposal-citation" className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px]">
                          <span className={proposal.ownDomain ? 'text-emerald-800' : 'text-amber-800'}>
                            {proposal.ownDomain
                              ? 'Found on the landlord\'s own site'
                              : 'Found on a third-party page - read it before saving'}
                          </span>
                          <a href={proposal.sourceUrl} target="_blank" rel="noopener noreferrer"
                            data-testid="proposal-source-link" className="text-blue-700 hover:underline">
                            {proposal.sourceUrl}
                          </a>
                        </div>
                      )}
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <input data-testid="landlord-contact-name" placeholder="Name (required)"
                          value={capture.name} onChange={(e) => setCapture((c) => ({ ...c, name: e.target.value }))}
                          className="rounded border border-gray-300 px-2 py-1 text-[13px]" />
                        <input data-testid="landlord-contact-title" placeholder="Title"
                          value={capture.title} onChange={(e) => setCapture((c) => ({ ...c, title: e.target.value }))}
                          className="rounded border border-gray-300 px-2 py-1 text-[13px]" />
                        <input data-testid="landlord-contact-phone" placeholder="Phone"
                          value={capture.phone} onChange={(e) => setCapture((c) => ({ ...c, phone: e.target.value }))}
                          className="rounded border border-gray-300 px-2 py-1 text-[13px]" />
                        <input data-testid="landlord-contact-email" placeholder="Email"
                          value={capture.email} onChange={(e) => setCapture((c) => ({ ...c, email: e.target.value }))}
                          className="rounded border border-gray-300 px-2 py-1 text-[13px]" />
                        <input data-testid="landlord-contact-source" placeholder="Where you found them (URL)"
                          value={capture.source} onChange={(e) => setCapture((c) => ({ ...c, source: e.target.value }))}
                          className="rounded border border-gray-300 px-2 py-1 text-[13px] sm:col-span-2" />
                      </div>
                      {captureError && (
                        <div className="mt-1.5 text-[12.5px] text-red-700">{captureError}</div>
                      )}
                      <div className="mt-2 flex items-center gap-1.5">
                        <button type="button" data-testid="save-landlord-contact"
                          onClick={() => void saveCapture(r)} disabled={busy === `capture|${r.entity_id}`}
                          className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[13px] font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">
                          Save the contact
                        </button>
                        <button type="button" onClick={() => setCaptureFor(null)}
                          className="rounded px-2 py-1.5 text-[13px] text-gray-600 hover:text-gray-900">
                          Cancel
                        </button>
                        <span className="text-[12px] text-gray-500">
                          Saves here, and into GHL when there is a phone or an email.
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        </>)}
      </div>
    </Layout>
  );
}
